import { KEY_CODES, ALLOWED_IMAGE_FILE_TYPES, IMG_PREVIEW_WIDTH, FILE_MAX_SIZE } from 'lib/constants.js';
import { uuid } from 'lib/util';
import {
  getScaledImageDataURL,
  getRotatedImageBlob,
  getRotatedImageDataURL,
  getScaledImageBlob,
  dataURItoBlob,
  downloadCrossOriginImageBlob,
  sendFileToAmazon,
  downloadCrossOriginImage,
} from 'lib/files.js';
import NodeFile from 'lib/node-file';

class NodeImage {

  async initialize({ nodeUUID, isNewImage, file, canvas }) {
    this.node = store.getNode(nodeUUID);
    if (isNewImage) {
      await this.setupNewImage(file,canvas);
    } else if (this.node.attachedFile) {
      this.nodeFile = new NodeFile({
        display_properties: Object.assign({size: canvas.defaultImageSize},this.node.attachedFile.display_properties),
        file_attributes: Object.assign({},this.node.attachedFile.file_attributes),
        file_size: this.node.attachedFile.file_size,
        file_type: this.node.attachedFile.file_type,
        file_url: this.node.attachedFile.file_url,
        uuid: this.node.attachedFile.uuid,
        preview_url: this.node.attachedFile.preview_url,
      });
    }
  }

  static async create({ nodeUUID, isNewImage, file, canvas }) {
    const nodeImage = new NodeImage();
    await nodeImage.initialize({ nodeUUID, isNewImage, file, canvas });
    return nodeImage;
  }

  async setupNewImage(file,canvas) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {

        const img = new Image();
        img.onload = () => {
          this.nodeFile.file = file;
          this.nodeFile.file_attributes = {
            width: img.width,
            height: img.height,
          };
          this.nodeFile.display_properties = { size: canvas.defaultImageSize };
          this.nodeFile.file_size = file.size;
          this.nodeFile.file_type = file.type;

          if (file.type != 'image/gif') {
            this.nodeFile.preview_file = getScaledImageBlob(img, file.type);
          }
          resolve();
        };

        this.nodeFile = new NodeFile({});
        this.nodeFile.file_url = e.target.result;
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  getDisplayProperties(canvas) {
    if (this.nodeFile && this.nodeFile.display_properties) {
      return this.nodeFile.display_properties;
    } else {
      return { size: canvas ? canvas.defaultImageSize : 'large' };
    }
  }

  rotateDisplayProperties() {
    if (this.nodeFile && this.nodeFile.display_properties && this.nodeFile.display_properties.width && this.nodeFile.display_properties.height) {
      const { height, width } = this.nodeFile.display_properties;
      this.nodeFile.display_properties.width = height;
      this.nodeFile.display_properties.height = width;
    }
  }

  getFileAttributes() {
    if (this.nodeFile && this.nodeFile.file_attributes) {
      return this.nodeFile.file_attributes;
    }
  }

  setFileAttributes({ width, height }) {
    this.nodeFile.file_attributes.width = width;
    this.nodeFile.file_attributes.height = height;
  }

  getFileSize() {
    if (this.nodeFile && this.nodeFile.file_size) {
      return this.nodeFile.file_size;
    }
  }

  getFileType() {
    if (this.nodeFile && this.nodeFile.file_type) {
      return this.nodeFile.file_type;
    } else {
      return 'image/png';
    }
  }

  getFileUrl() {
    if (this.nodeFile && this.nodeFile.file_url) {
      return this.nodeFile.file_url;
    } else {
      return '';
    }
  }

  getPreviewFileUrl() {
    if (this.nodeFile && this.nodeFile.preview_url) {
      return this.nodeFile.preview_url;
    } else {
      return null;
    }
  }

  getFile() {
    return this.nodeFile.file;
  }

  getPreviewFile() {
    return this.nodeFile.preview_file;
  }

  async getFileBlob() {
    if (this.nodeFile.file) {
      return this.nodeFile.file;
    } else {
      const imageBlob = await downloadCrossOriginImageBlob(this.nodeFile.file_url, this.getFileType());
      return imageBlob;
    }
  }

  async getPreviewFileBlob() {
    if (this.nodeFile && this.nodeFile.preview_file) {
      return this.nodeFile.preview_file;
    } else {
      const imageBlob = await downloadCrossOriginImageBlob(this.nodeFile.preview_url, this.getFileType());
      return imageBlob;
    }
  }

  async rotateImage() {
    return new Promise(async (resolve, reject) => {
      this.nodeFile.file = await this.getFileBlob();
      if (this.nodeFile.preview_url) {
        this.nodeFile.preview_file = await this.getPreviewFileBlob();
      }

      const previewReader = new FileReader();
      previewReader.onload = e => {
        const img = new Image();
        img.onload = () => {
          const { dataURL } = getRotatedImageDataURL(img, this.getFileType(), 270);
          this.nodeFile.preview_file = dataURItoBlob(dataURL);
          resolve(dataURL);
        };
        img.src = e.target.result;
      };

      const reader = new FileReader();
      reader.onload = e => {
        const img = new Image();
        img.onload = () => {
          const { dataURL, height, width } = getRotatedImageDataURL(img, this.getFileType(), 270);
          this.nodeFile.file = dataURItoBlob(dataURL);
          this.setFileAttributes({ width, height });
          this.rotateDisplayProperties();
          if (this.getFileType() !== 'image/gif') {
            previewReader.readAsDataURL(this.nodeFile.preview_file);
          } else {
            resolve(dataURL);
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(this.nodeFile.file);
    });
  }

  getFileName() {
    switch (this.nodeFile.file_type) {
      case 'image/jpeg':
        return 'image.jpeg';
      case 'image/png':
        return 'image.png';
      case 'image/x-icon':
        return 'image.ico';
      default:
        return 'image.png';
    }
  }

  getUUID() {
    return this.nodeFile.uuid;
  }

}

export { NodeImage };



// WEBPACK FOOTER //
// ./starling/src/lib/node-image.js